import React, { useState, useMemo, useRef, useCallback } from "react";
import { IconButton, SvgIcon, LinearProgress } from "@mui/material";
import moment from "moment";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { mdiPlay, mdiPause } from "@mdi/js";
import SettingsMenu from "./SettingsMenu";
import { SkipNextBackward, SkipNextForward, Rewind, FastForward } from "orion-components/CBComponents/Icons";
import VideoDateRangeSelector from "./VideoDateRangeSelector";
import TimeLineControls from "./TimeLineControls";
import VideoScalePicker from "./VideoScalePicker";
import { useSelector } from "react-redux";
import { getDir } from "orion-components/i18n/Config/selector";
import { useEffect } from "react";
import PropTypes from "prop-types";
import { cameraService } from "client-app-core";
import EventBus from "orion-components/SharedComponents/EventBus";
import { getWebrtcSettingByStreamAndValue } from "orion-components/GlobalData/Selectors";
import SpeedStack from "./SpeedStack";
import { publishRecordedSeek, publishRecordedSpeed } from "./playbackCommands";

let streamToBeRemoved = "";
let key = 0;

const RecordedBottomBar = ({
	cameraId,
	feedId,
	instanceId,
	playbackStreamName,
	isOnAlertProfile,
	alertDateTime,
	playerType,
	defaultToRecorded,
	dialogContainerRef,
	playbackStartOffset,
	initialRecordedDuration,
	initialRecordedTimeUnit,
	registerRecordedNavigator,
	selectedClip,
	onRecordedTimeframeChange
}) => {
	const videoDateRangeSelSetStartDateExternallyRef = useRef(null);
	const [videoLoadProgress, setVideoLoadProgress] = useState(0);
	const [isVideoPlaying, setIsVideoPlaying] = useState(false);
	const [duration, setDuration] = useState();
	const [durationSet, setDurationSet] = useState(false);
	const [scale, setScale] = useState(null);
	const [currentTime, setCurrentTime] = useState(null);
	const [startTime, setStartTime] = useState(null);
	const [startDateTime, setStartDateTime] = useState(null);
	const [endDateTime, setEndDateTime] = useState(null);
	const [speed, setSpeed] = useState(1);
	const [isMouseOverChevronLeft, setIsMouseOverChevronLeft] = useState(false);
	const [isMouseOverChevronRight, setIsMouseOverChevronRight] = useState(false);
	const [componentKey, setComponentKey] = useState(0);
	const [isSettingsMenuOpen, setIsSettingsMenuOpen] = useState(false);
	const [supportedSpeeds, setSupportedSpeeds] = useState([-16, -8, -4, -2, -1, -0.5, 0.5, 1, 2, 4, 8, 16]);
	const [formatedDuration, setFormatedDuration] = useState(30);
	const [formatedDurationUnit, setFormatedDurationUnit] = useState("minutes");
	const [dateRangeSetterVersion, setDateRangeSetterVersion] = useState(0);

	const dir = useSelector(state => getDir(state));

	const containerRef = useRef(null);
	const timeLineRef = useRef(null);
	const videoBottomBarRef = useRef(null);
	const pendingPlaybackStateRestoreRef = useRef(null);
	/**
	 * Right now recordedMode is always "recorded", but we will maintain the ability to
	 * switch to "clip" mode in the future.
	 */
	const recordedMode = "recorded";

	useEffect(() => {
		// If start and end date time are set, calculate the actual duration and unit to display in the JumpToDifferentTime component.
		if (startDateTime && endDateTime) {
			let duration, unit;
			const diffSeconds = Math.floor((new Date(endDateTime).getTime() - new Date(startDateTime).getTime()) / 1000);
			if (diffSeconds < 60) {
				duration = diffSeconds;
				unit = "seconds";
			} else if (diffSeconds < 3600) {
				duration = diffSeconds / 60;
				unit = "minutes";
			} else {
				duration = diffSeconds / 3600;
				unit = "hours";
			}
			setFormatedDuration(duration);
			setFormatedDurationUnit(unit);
			setDuration(diffSeconds / 60);
		}
	}, [startDateTime, endDateTime]);

	useEffect(() => {
		EventBus.subscribe("getStreamToBeRemoved", getStreamToBeRemoved);

		EventBus.subscribe("RecordedBottomBar_startVideo", startVideo);
		EventBus.subscribe("RecordedBottomBar_loadProgress", setLoadProgress);

		EventBus.subscribe("NewVideoPlayer_VideoDurationPicker_SetVideoDurationExternally", setVideoDurationExternally);

		EventBus.subscribe("VideoPlayer_CapabilitiesReady", handlePlayerCapabilitiesReady);

		return () => {
			EventBus.unsubscribe("getStreamToBeRemoved", getStreamToBeRemoved);

			EventBus.unsubscribe("RecordedBottomBar_startVideo", startVideo);
			EventBus.unsubscribe("RecordedBottomBar_loadProgress", setLoadProgress);

			EventBus.unsubscribe(
				"NewVideoPlayer_VideoDurationPicker_SetVideoDurationExternally",
				setVideoDurationExternally
			);

			EventBus.unsubscribe("VideoPlayer_CapabilitiesReady", handlePlayerCapabilitiesReady);
		};
	}, []);

	useEffect(() => {
		if (defaultToRecorded) {
			const multiplier =
				initialRecordedTimeUnit === "seconds" ? 1 : initialRecordedTimeUnit === "minutes" ? 60 : 3600;
			const durationSeconds = isNaN(initialRecordedDuration)
				? 20
				: parseInt(initialRecordedDuration, 10) * multiplier;
			setDuration(durationSeconds / 60);
		} else if (isOnAlertProfile) {
			const multiplier =
				initialRecordedTimeUnit === "seconds" ? 1 : initialRecordedTimeUnit === "minutes" ? 60 : 3600;
			const durationSeconds = isNaN(initialRecordedDuration)
				? 20
				: parseInt(initialRecordedDuration, 10) * multiplier;
			setDuration(durationSeconds / 60);
		} else {
			setDuration(10); // default 10 minutes
		}
		setDurationSet(true);
	}, [defaultToRecorded, initialRecordedDuration, initialRecordedTimeUnit, isOnAlertProfile]);

	const toggleSettingsMenu = value => {
		setIsSettingsMenuOpen(value);
	};

	const getDuration = value => {
		setDuration(value);
	};

	const getScale = value => {
		setScale(value);
	};

	const handlePlayerCapabilitiesReady = data => {
		if (data.instanceId !== instanceId) {
			return;
		}
		if (data.supportedSpeeds) {
			console.debug("[RecordedBottomBar] Received supported speeds from player:", data.supportedSpeeds);
			setSupportedSpeeds(data.supportedSpeeds);
		} else {
			console.debug("[RecordedBottomBar] Received capabilities without supported speeds, using defaults");
			setSupportedSpeeds([-16, -8, -4, -2, -1, -0.5, 0.5, 1, 2, 4, 8, 16]);
		}
	};

	const setVideoDurationExternally = data => {
		if (data.instanceId !== instanceId) {
			return;
		}

		getDuration(data.duration);
		setSpeed(1); // reset speed to 1x

		switch (playerType) {
			case "avigilon":
				EventBus.publish("AvigilonRecordedWebRTCPlayerWrapper_renderNewComponentInstance", { instanceId });
				break;
			case "vicads":
				EventBus.publish("VicadsRecordedWebRTCPlayerWrapper_renderNewComponentInstance", { instanceId });
				break;
			case "flashphoner":
				EventBus.publish("PlaybackRecordedWebRTCPlayerWrapper_renderNewComponentInstance");
				break;
			case "native":
			case "ws-jpeg":
				// These players are driven through Universal_PlaybackCommand and do not need a
				// player-specific re-render event when only the recorded duration changes.
				break;
			default:
				console.error("Unknown camera system");
				return;
		}
	};

	const getStartTime = useCallback(value => {
		setStartTime(value);
		setCurrentTime(null);
		key++;
		setComponentKey(key);
	}, []);

	const getStartAndEndDateTime = useCallback((start, end) => {
		setStartDateTime(start);
		setEndDateTime(end);
	}, []);

	const getStreamToBeRemoved = name => {
		streamToBeRemoved = name;
	};

	useEffect(() => {
		if (defaultToRecorded && !isOnAlertProfile) {
			const parsedDate = new Date();
			handleSetStartDateExternally({
				instanceId,
				userSelectedDateTime: parsedDate.toString(),
				durationVal: initialRecordedDuration,
				durationType: initialRecordedTimeUnit
			});
			let finalDuration = initialRecordedDuration;
			if (initialRecordedTimeUnit === "seconds") {
				finalDuration = finalDuration / 60;
			}
			if (initialRecordedTimeUnit === "hours") {
				finalDuration = finalDuration * 60;
			}
			EventBus.publish("NewVideoPlayer_VideoDurationPicker_SetVideoDurationExternally", {
				instanceId,
				duration: finalDuration
			});
		}
	}, [defaultToRecorded, initialRecordedTimeUnit, initialRecordedDuration, isOnAlertProfile]);

	useEffect(() => {
		return () => {
			if (streamToBeRemoved !== "") {
				cameraService.removeWowzaStream(cameraId, true, err => {
					if (err) {
						console.log("ERROR", err);
						return;
					}
				});
			}
		};
	}, []);

	const styles = useMemo(() => {
		return {
			container: {
				display: "flex",
				height: "100%",
				width: "100%",
				alignItems: "center"
			},
			leftChevron: {
				height: 32,
				width: 14,
				background: "#000000"
			},
			chevronContainer: {
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				width: "100%",
				height: "100%"
			},
			chevronWrapper: {
				padding: 0,
				height: 5.1,
				width: 3
			},
			playbackControls: {
				position: "absolute",
				bottom: 0,
				display: "flex",
				height: "100%",
				justifyContent: "space-between",
				alignItems: "center",
				background: "#000",
				zIndex: 2,
				...(dir === "ltr" && { left: 14 }),
				...(dir === "rtl" && { right: 14 })
			},
			rightChevron: {
				height: 32,
				width: 14,
				background: "#000000"
			},
			timeLineWrapper: {
				width: "100%",
				height: "100%",
				overflowX: "hidden"
			},
			settingsSection: {
				position: "absolute",
				bottom: 0,
				width: "auto",
				height: "100%",
				display: "flex",
				alignItems: "center",
				background: "#000",
				zIndex: 2,
				...(dir === "ltr" && { right: 14, paddingRight: 6 }),
				...(dir === "rtl" && { left: 14, paddingLeft: 6 })
			}
		};
	}, [dir]);

	const connected = useSelector(state => getWebrtcSettingByStreamAndValue(state, playbackStreamName, "connected"));

	useEffect(() => {
		if (connected) {
			setIsVideoPlaying(true);
		}
	}, [connected]);

	const publishPauseCommand = useCallback(() => {
		switch (playerType) {
			case "avigilon":
				EventBus.publish("AvigilonRecordedWebRTCPlayer_applyPlaybackCommand", { instanceId, command: "pause" });
				break;
			case "vicads":
				EventBus.publish("VicadsRecordedWebRTCPlayer_applyPlaybackCommand", { instanceId, command: "pause" });
				break;
			case "flashphoner":
				EventBus.publish("Playback_stopVideoStreaming");
				break;
			case "native":
			case "ws-jpeg":
				EventBus.publish("Universal_PlaybackCommand", {
					instanceId,
					command: "pause"
				});
				break;
			default:
				break;
		}
	}, [instanceId, playerType]);

	const publishPlayCommand = useCallback((playbackSpeed = speed) => {
		switch (playerType) {
			case "avigilon":
				EventBus.publish("AvigilonRecordedWebRTCPlayer_applyPlaybackCommand", { instanceId, command: "play" });
				break;
			case "vicads":
				EventBus.publish("VicadsRecordedWebRTCPlayer_applyPlaybackCommand", { instanceId, command: "play" });
				break;
			case "flashphoner":
				EventBus.publish("Playback_restartstopVideoStreaming");
				break;
			case "native":
			case "ws-jpeg":
				EventBus.publish("Universal_PlaybackCommand", {
					instanceId,
					command: "play",
					speed: playbackSpeed
				});
				break;
			default:
				break;
		}
	}, [instanceId, playerType, speed]);

	const restorePlaybackState = useCallback(
		playbackState => {
			if (!playbackState) {
				return;
			}

			if (!playbackState.playing) {
				publishPauseCommand();
				setIsVideoPlaying(false);
				return;
			}

			publishPlayCommand(playbackState.speed);
			if (playbackState.speed !== 1) {
				publishRecordedSpeed({ instanceId, speed: playbackState.speed });
			}
			setSpeed(playbackState.speed);
			setIsVideoPlaying(true);
		},
		[instanceId, publishPauseCommand, publishPlayCommand]
	);

	const startVideo = data => {
		if (typeof data === "boolean") {
			setIsVideoPlaying(data);
			if (data && pendingPlaybackStateRestoreRef.current) {
				const playbackState = pendingPlaybackStateRestoreRef.current;
				pendingPlaybackStateRestoreRef.current = null;
				restorePlaybackState(playbackState);
			}
		} else {
			if (data.instanceId === instanceId) {
				setIsVideoPlaying(data.playing);
				if (data.playing && pendingPlaybackStateRestoreRef.current) {
					const playbackState = pendingPlaybackStateRestoreRef.current;
					pendingPlaybackStateRestoreRef.current = null;
					restorePlaybackState(playbackState);
				}
			}
		}
	};

	const setLoadProgress = data => {
		if (data.instanceId === instanceId) {
			setVideoLoadProgress(data.progress);
		}
	};

	const moveForward = byScale => {
		// byScale = true, move forward one scale, else move forward one frame (scale/10)
		if (isVideoPlaying || currentTime >= endDateTime) {
			return;
		}

		if (playerType === "native") {
			// Native player supports stepping exactly on frame forward or backward
			EventBus.publish("Universal_PlaybackCommand", {
				instanceId,
				command: "stepForward"
			});
			return;
		}

		let newTime = moment(currentTime).add(byScale ? scale : scale / 10, "m");
		if (newTime > endDateTime) {
			newTime = endDateTime;
		}

		publishRecordedSeek({ instanceId, time: newTime.toDate() });
	};

	const moveBackward = byScale => {
		// byScale = true, move backward one scale, else move backward one frame (scale/10)
		if (isVideoPlaying || currentTime <= startDateTime) {
			// We allow operation only in paused mode
			return;
		}

		if (playerType === "native") {
			// Native player supports stepping exactly on frame forward or backward
			EventBus.publish("Universal_PlaybackCommand", {
				instanceId,
				command: "stepBackward"
			});
			return;
		}

		let newTime = moment(currentTime).subtract(byScale ? scale : scale / 10, "m");
		if (newTime < startDateTime) {
			newTime = startDateTime;
		}

		publishRecordedSeek({ instanceId, time: newTime.toDate() });
	};

	const increaseSpeed = () => {
		if (!isVideoPlaying) {
			// We allow operation only in play mode
			return;
		}

		// Find current speed in supported speeds array
		const currentIndex = supportedSpeeds.indexOf(speed);
		if (currentIndex === -1 || currentIndex === supportedSpeeds.length - 1) {
			// Speed not found or already at max
			return;
		}

		const newSpeed = supportedSpeeds[currentIndex + 1];

		publishRecordedSpeed({ instanceId, speed: newSpeed });
		setSpeed(newSpeed);
	};

	const decreaseSpeed = () => {
		if (!isVideoPlaying) {
			// We allow operation only in play mode
			return;
		}

		// Find current speed in supported speeds array
		const currentIndex = supportedSpeeds.indexOf(speed);
		if (currentIndex === -1 || currentIndex === 0) {
			// Speed not found or already at min
			return;
		}

		const newSpeed = supportedSpeeds[currentIndex - 1];

		publishRecordedSpeed({ instanceId, speed: newSpeed });

		setSpeed(newSpeed);
	};

	const handleSetStartDateExternally = useCallback(payload => {
		if (videoDateRangeSelSetStartDateExternallyRef.current) {
			videoDateRangeSelSetStartDateExternallyRef.current(payload);
		}
	}, []);

	const registerSetStartDateExternally = useCallback(fn => {
		videoDateRangeSelSetStartDateExternallyRef.current = fn;
		setDateRangeSetterVersion(version => version + 1);
	}, []);

	useEffect(() => {
		if (!selectedClip) {
			return;
		}

		const clipStartTime = new Date(selectedClip.startTime);
		const clipEndTime = new Date(selectedClip.endTime);
		if (
			Number.isNaN(clipStartTime.getTime()) ||
			Number.isNaN(clipEndTime.getTime()) ||
			clipEndTime <= clipStartTime
		) {
			return;
		}

		const startMs = clipStartTime.getTime();
		const endMs = clipEndTime.getTime();
		const durationSeconds = Math.max((endMs - startMs) / 1000, 1);

		setDuration(durationSeconds / 60);
		setDurationSet(true);
		setScale(durationSeconds / 60);
		setStartTime(clipStartTime);
		setCurrentTime(clipStartTime);
		setStartDateTime(startMs);
		setEndDateTime(endMs);
		setSpeed(1);
		setComponentKey(prevKey => prevKey + 1);

		switch (playerType) {
			case "avigilon":
				EventBus.publish("AvigilonRecordedWebRTCPlayerWrapper_setPlayBackTimeFrame", {
					instanceId,
					startDateTimeVal: startMs,
					endDateTimeVal: endMs,
					recordedMode: "recorded"
				});
				break;
			case "vicads":
				EventBus.publish("VicadsRecordedWebRTCPlayerWrapper_setPlayBackTimeFrame", {
					instanceId,
					startDateTimeVal: startMs,
					endDateTimeVal: endMs,
					recordedMode: "recorded"
				});
				break;
			case "flashphoner":
				EventBus.publish("PlaybackRecordedWebRTCPlayerWrapper_setPlayBackTimeFrame", {
					startDateTimeVal: startMs,
					endDateTimeVal: endMs
				});
				break;
			case "native":
				// The selected clip is synchronized with VideoDateRangeSelector below.
				// Its playback effect publishes the native setCursor command, so publishing
				// here would start a duplicate recorded-playback request.
				break;
			case "ws-jpeg":
				EventBus.publish("Universal_PlaybackCommand", {
					instanceId,
					command: "setCursor",
					recordedMode: "recorded",
					time: startMs,
					duration: durationSeconds,
					speed: 1
				});
				break;
			default:
				console.error("Unknown camera system");
				return;
		}

		handleSetStartDateExternally({
			instanceId,
			startDateTimeVal: startMs,
			endDateTimeVal: endMs,
			isFor: "startDate"
		});
	}, [dateRangeSetterVersion, handleSetStartDateExternally, instanceId, playerType, selectedClip]);

	useEffect(() => {
		if (!registerRecordedNavigator || !videoDateRangeSelSetStartDateExternallyRef.current) {
			return;
		}

		const navigateToRecordedTimestamp = timestamp => {
			const targetTime = new Date(timestamp);
			if (Number.isNaN(targetTime.getTime())) {
				return;
			}

			const playbackState = { playing: isVideoPlaying, speed };
			const startMs = startDateTime ? new Date(startDateTime).getTime() : NaN;
			const endMs = endDateTime ? new Date(endDateTime).getTime() : NaN;
			const targetMs = targetTime.getTime();
			const isWithinCurrentRange =
				Number.isFinite(startMs) && Number.isFinite(endMs) && targetMs >= startMs && targetMs <= endMs;

			if (isWithinCurrentRange) {
				publishRecordedSeek({ instanceId, time: targetTime });
				restorePlaybackState(playbackState);
				setCurrentTime(targetTime);
				return;
			}

			const durationMs = Number.isFinite(duration) && duration > 0 ? duration * 60 * 1000 : 10 * 60 * 1000;
			const endTime = new Date(targetMs + durationMs);
			pendingPlaybackStateRestoreRef.current = playbackState;

			handleSetStartDateExternally({
				instanceId,
				startDateTimeVal: targetMs,
				endDateTimeVal: endTime.getTime(),
				isFor: "startDate"
			});
			setCurrentTime(targetTime);
		};

		registerRecordedNavigator(navigateToRecordedTimestamp);

		return () => {
			registerRecordedNavigator(null);
		};
	}, [
		dateRangeSetterVersion,
		duration,
		endDateTime,
		handleSetStartDateExternally,
		instanceId,
		registerRecordedNavigator,
		restorePlaybackState,
		isVideoPlaying,
		speed,
		startDateTime
	]);
	return (
		<div
			className="bccVideoBottomBarWrapper bccRecordedBottomBarWrapper"
			style={{ zIndex: 1901 }}
			ref={videoBottomBarRef} data-testid="orion-components-cbcomponents-new-video-player-components-recorded-recorded-bottom-bar-div"
		>
			<div ref={containerRef} style={styles.container}>
				{recordedMode === "recorded" && (
					<div style={styles.leftChevron}>
						<div
							style={{
								...styles.chevronContainer,
								visibility: `${isVideoPlaying ||
									(dir === "ltr" && currentTime?.getTime() === startDateTime?.valueOf()) ||
									(dir === "rtl" && currentTime?.getTime() === endDateTime?.valueOf())
									? "hidden"
									: "visible"
									}`,
								background: !isMouseOverChevronLeft ? "#000000" : "#C9C9C9"
							}}
							onMouseEnter={() => {
								setIsMouseOverChevronLeft(!isMouseOverChevronLeft);
							}}
							onMouseLeave={() => {
								setIsMouseOverChevronLeft(!isMouseOverChevronLeft);
							}} data-testid="orion-components-cbcomponents-new-video-player-components-recorded-recorded-bottom-bar-div-2"
						>
							<IconButton
								sx={{ ...styles.chevronWrapper, color: !isMouseOverChevronLeft ? "#fff" : "#000000" }}
								onClick={() => (dir === "ltr" ? moveBackward(true) : moveForward(true))} data-testid="orion-components-cbcomponents-new-video-player-components-recorded-recorded-bottom-bar-icon-button"
							>
								{dir === "ltr" ? <ChevronLeftIcon /> : <ChevronRightIcon />}
							</IconButton>
						</div>
					</div>
				)}

				<div style={styles.timeLineWrapper}>
					{startTime !== null && durationSet && (
						<TimeLineControls
							key={componentKey}
							instanceId={instanceId}
							recordedMode={recordedMode}
							videoDurationInMinutes={duration}
							videoScaleInMinutes={scale}
							startTime={startTime}
							currentTime={currentTime}
							isPlaying={isVideoPlaying}
							cameraId={cameraId}
							setCurrentTime={setCurrentTime}
						/>
					)}
				</div>
				{recordedMode === "recorded" && (
					<div style={styles.rightChevron}>
						<div
							style={{
								...styles.chevronContainer,
								visibility: `${isVideoPlaying ||
									(dir === "ltr" && currentTime?.getTime() === endDateTime?.valueOf()) ||
									(dir === "rtl" && currentTime?.getTime() === startDateTime?.valueOf())
									? "hidden"
									: "visible"
									}`,
								background: !isMouseOverChevronRight ? "#000000" : "#C9C9C9"
							}}
							onMouseEnter={() => {
								setIsMouseOverChevronRight(!isMouseOverChevronRight);
							}}
							onMouseLeave={() => {
								setIsMouseOverChevronRight(!isMouseOverChevronRight);
							}} data-testid="orion-components-cbcomponents-new-video-player-components-recorded-recorded-bottom-bar-div-3"
						>
							<IconButton
								sx={{ ...styles.chevronWrapper, color: !isMouseOverChevronRight ? "#fff" : "#000000" }}
								onClick={() => (dir === "ltr" ? moveForward(true) : moveBackward(true))} data-testid="orion-components-cbcomponents-new-video-player-components-recorded-recorded-bottom-bar-icon-button-2"
							>
								{dir === "ltr" ? <ChevronRightIcon /> : <ChevronLeftIcon />}
							</IconButton>
						</div>
					</div>
				)}

				{recordedMode === "recorded" && (
					<div ref={timeLineRef} style={styles.playbackControls}>
						<div style={{ float: dir === "ltr" ? "left" : "right", width: "auto" }}>
							<div
								style={{
									display: "flex",
									...(!isVideoPlaying && { alignItems: "center" }),
									width: "100%",
									height: "100%"
								}}
							>
								<div
									style={{
										marginInlineEnd: 8.7
									}}
									onClick={() => {
										if (isVideoPlaying) {
											if (dir === "ltr") {
												decreaseSpeed();
											} else {
												increaseSpeed();
											}
										} else {
											if (dir === "ltr") {
												moveBackward(false);
											} else {
												moveForward(false);
											}
										}
									}} data-testid="orion-components-cbcomponents-new-video-player-components-recorded-recorded-bottom-bar-div-4"
								>
									{!isVideoPlaying ? (
										dir === "ltr" ? (
											<div>
												<SkipNextBackward fillColor="#fff" />
											</div>
										) : (
											<div>
												<SkipNextForward fillColor="#fff" />
											</div>
										)
									) : dir === "ltr" ? (
										<SpeedStack
											show={speed < 0}
											text={`${speed}x`}
											icon={
												<Rewind fillColor="#fff" customStyles={{ transform: "scale(1.1)" }} />
											}
										/>
									) : (
										<SpeedStack
											show={speed > 0}
											text={`+${speed}x`}
											icon={
												<FastForward
													fillColor="#fff"
													customStyles={{ transform: "scale(1.1)" }}
												/>
											}
										/>
									)}
								</div>
								<div>
									<IconButton
										sx={{ zIndex: 2 }}
										onClick={() => {
											setIsVideoPlaying(!isVideoPlaying);
											if (isVideoPlaying) {
												publishPauseCommand();
											} else {
												publishPlayCommand();
											}
										}} data-testid="orion-components-cbcomponents-new-video-player-components-recorded-recorded-bottom-bar-icon-button-3"
									>
										<div
											className="bcc-playBack-play-pause-button"
											style={{
												backgroundColor: !isVideoPlaying ? "#4DB5F4" : "#BABABA",
												display: "flex",
												alignItems: "center",
												justifyContent: "center"
											}}
										>
											<SvgIcon
												style={{
													width: 10,
													height: 9.93,
													color: "#FFFFFF",
													transform: "scale(2)"
												}}
											>
												<path d={!isVideoPlaying ? mdiPlay : mdiPause} />
											</SvgIcon>
										</div>
									</IconButton>
								</div>

								<div
									style={{
										marginInlineStart: 7.31
									}}
									onClick={() => {
										if (isVideoPlaying) {
											if (dir === "ltr") {
												increaseSpeed();
											} else {
												decreaseSpeed();
											}
										} else {
											if (dir === "ltr") {
												moveForward(false);
											} else {
												moveBackward(false);
											}
										}
									}} data-testid="orion-components-cbcomponents-new-video-player-components-recorded-recorded-bottom-bar-div-5"
								>
									{!isVideoPlaying ? (
										dir === "ltr" ? (
											<div>
												<SkipNextForward fillColor="#fff" />
											</div>
										) : (
											<div>
												<SkipNextBackward fillColor="#fff" />
											</div>
										)
									) : dir === "ltr" ? (
										<SpeedStack
											show={speed > 0}
											text={`+${speed}x`}
											icon={
												<FastForward
													fillColor="#fff"
													customStyles={{ transform: "scale(1.1)" }}
												/>
											}
										/>
									) : (
										<SpeedStack
											show={speed < 0}
											text={`${speed}x`}
											icon={
												<Rewind fillColor="#fff" customStyles={{ transform: "scale(1.1)" }} />
											}
										/>
									)}
								</div>
							</div>
						</div>
					</div>
				)}

				{recordedMode === "recorded" && (
					<div style={styles.settingsSection}>
						<div
							style={{
								marginLeft: 8,
								width: "auto",
								marginRight: 8,
								zIndex: !isSettingsMenuOpen ? 2 : 1
							}}
						>
							<VideoScalePicker duration={duration} getScale={getScale} />
						</div>
						<SettingsMenu
							duration={duration}
							instanceId={instanceId}
							cameraId={cameraId}
							feedId={feedId}
							currentTime={currentTime}
							toggleSettingsMenu={toggleSettingsMenu}
							setVideoDurationExternally={setVideoDurationExternally}
							setStartDateExternally={handleSetStartDateExternally}
							formatedDuration={formatedDuration}
							formatedDurationUnit={formatedDurationUnit}
							startDateTime={startDateTime}
							dialogContainerRef={dialogContainerRef}
						/>
					</div>
				)}
			</div>
			{recordedMode === "recorded" &&
				(playerType === "vicads" || playerType === "avigilon") &&
				videoLoadProgress < 100 && (
					<div style={{ position: "absolute", bottom: 32, width: "100%", height: 5 }}>
						<LinearProgress
							dir={dir}
							style={{ height: 5, width: "100%" }}
							variant="determinate"
							value={videoLoadProgress}
						/>
					</div>
				)}
			{durationSet && recordedMode === "recorded" && (
				<VideoDateRangeSelector
					instanceId={instanceId}
					duration={duration}
					getStartTime={getStartTime}
					isOnAlertProfile={isOnAlertProfile}
					alertDateTime={alertDateTime}
					getStartAndEndDateTime={getStartAndEndDateTime}
					getDuration={getDuration}
					playbackStartOffset={playbackStartOffset}
					playerType={playerType}
					recordedMode={recordedMode}
					initialRecordedDuration={initialRecordedDuration}
					initialRecordedTimeUnit={initialRecordedTimeUnit}
					setVideoDurationExternally={setVideoDurationExternally}
					registerSetStartDateExternally={registerSetStartDateExternally}
					formatedDuration={formatedDuration}
					formatedDurationUnit={formatedDurationUnit}
					onRecordedTimeframeChange={onRecordedTimeframeChange} data-testid="orion-components-cbcomponents-new-video-player-components-recorded-recorded-bottom-bar-video-date-range-selector"
					selectedClip={selectedClip}
				/>
			)}
		</div>
	);
};

RecordedBottomBar.propTypes = {
	cameraId: PropTypes.string,
	feedId: PropTypes.string,
	instanceId: PropTypes.string,
	isOnAlertProfile: PropTypes.bool,
	alertDateTime: PropTypes.any,
	defaultToRecorded: PropTypes.bool,
	dialogContainerRef: PropTypes.shape({
		current: PropTypes.any
	}),
	initialRecordedDuration: PropTypes.string,
	initialRecordedTimeUnit: PropTypes.string,
	playbackStreamName: PropTypes.string,
	playerType: PropTypes.string,
	playbackStartOffset: PropTypes.number,
	registerRecordedNavigator: PropTypes.func,
	selectedClip: PropTypes.object,
	onRecordedTimeframeChange: PropTypes.func
};

RecordedBottomBar.defaultProps = {
	selectedClip: null
};

export default RecordedBottomBar;
